import React, { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import { attachWidgetBridge, postToWidget, INBOUND, OUTBOUND } from "../visuals/widgetBridge";
import buildWidgetDocument from "../visuals/widgetDocument";
import cssVar from "../visuals/cssVar";

/**
 * Sandboxed HTML widget — the last resort, for interactive explainers that a
 * typed block and a sanitized diagram genuinely cannot express.
 *
 * sandbox="allow-scripts" WITHOUT allow-same-origin gives the frame an opaque
 * origin: no access to our cookies, storage, or DOM. That is the actual
 * security boundary. Omitting allow-same-origin is not an oversight and must
 * never be "fixed" — adding it back would hand the frame our origin.
 *
 * KNOWN GAP: srcdoc inherits the embedding document's CSP, so this frame runs
 * under the app's policy rather than a stricter one of its own. A real
 * per-frame CSP requires serving from a separate origin with real headers.
 * Recorded in docs/DECISIONS.md.
 *
 * Streaming: html arrives via block_delta. srcdoc is rewritten on a ~100ms
 * debounce while streaming so something paints early, and scripts only run on
 * the final document once the stream completes.
 */
const REWRITE_MS = 100;
const INITIAL_HEIGHT = 180;

const readTokens = el => ({
  ink: cssVar("--cp-ink", el, "#111827"),
  ink2: cssVar("--cp-ink-2", el, "#4b5563"),
  ink3: cssVar("--cp-ink-3", el, "#9ca3af"),
  surface: cssVar("--cp-surface", el, "#ffffff"),
  bg: cssVar("--cp-bg", el, "#f6f7fb"),
  border: cssVar("--cp-border", el, "#e8eaf2"),
  blue: cssVar("--cp-blue", el, "#3953fb"),
  series1: cssVar("--cp-series-1", el, "#3953fb"),
  series2: cssVar("--cp-series-2", el, "#f59e0b"),
  series3: cssVar("--cp-series-3", el, "#29b969")
});

const WidgetBlock = observer(({ block, isStreaming }) => {
  const rootRef = useRef(null);
  const frameRef = useRef(null);
  const [height, setHeight] = useState(INITIAL_HEIGHT);
  const [doc, setDoc] = useState("");

  // Debounced rewrite: rebuilding srcdoc on every delta reloads the frame and
  // makes it flash.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDoc(buildWidgetDocument(block.html || "", readTokens(rootRef.current)));
    }, isStreaming ? REWRITE_MS : 0);
    return () => clearTimeout(timer);
  }, [block.html, isStreaming]);

  useEffect(
    () =>
      attachWidgetBridge({
        getFrame: () => frameRef.current,
        handlers: {
          [INBOUND.RESIZE]: message => setHeight(message.height),

          [INBOUND.SEND_PROMPT]: message => {
            if (!copilotStore.streaming) copilotStore.sendUserMessage(message.text);
          },

          // The parent decides, not the frame: scheme is already validated,
          // and the user still confirms before anything opens.
          [INBOUND.OPEN_LINK]: message => {
            // eslint-disable-next-line no-alert
            if (window.confirm(`Open ${message.url} in a new tab?`)) {
              window.open(message.url, "_blank", "noopener,noreferrer");
            }
          },

          // Data by reference only. The frame names a tableId; the parent
          // serves rows the server already agreed to for this conversation,
          // and never anything the frame did not have a reference to.
          [INBOUND.REQUEST_DATA]: message => {
            const table = copilotStore.tables.get(message.tableId);
            const frame = frameRef.current;
            if (!table) {
              postToWidget(frame, {
                type: OUTBOUND.ERROR,
                requestId: message.requestId,
                error: "unknown table"
              });
              return;
            }
            postToWidget(frame, {
              type: OUTBOUND.DATA,
              requestId: message.requestId,
              rows: table.rows.slice()
            });
          }
        }
      }),
    []
  );

  const sandbox = useMemo(() => "allow-scripts", []);

  if (!block.html) return null;

  return (
    <div className="cpVisual cpWidget" ref={rootRef}>
      {block.title && <div className="cpVisual__title">{block.title}</div>}
      <iframe
        ref={frameRef}
        className="cpWidget__frame"
        title={block.title || "Interactive widget"}
        // No allow-same-origin: the frame gets an opaque origin. Do not add it.
        sandbox={sandbox}
        srcDoc={doc}
        style={{ height: `${height}px` }}
      />
    </div>
  );
});

export default WidgetBlock;
